import { formatInTimeZone } from 'date-fns-tz';

import {
    resolveShiftStatus,
    type UnifiedShiftStatus,
} from '@apps/web/components/dashboard/shift_list_filters';
import { isActiveOutreach } from '@apps/web/components/outreaches/utils';
import { UNASSIGNED_CARE_COORDINATOR_ID } from '@apps/web/hooks/use_care_coordinator_filter';
import { comparePersonNames } from '@apps/web/lib/caregiver_name';
import {
    endOfMonthDateKey,
    formatCompactTime,
    shiftDateKey,
    startOfMonthDateKey,
    toDateKey,
    WEEKDAY_INDEX_BY_DAY,
} from '@apps/web/lib/date_utils';
import {
    getAcceptedCaregiversForShift,
    getAcceptedCountForShift,
    getEffectiveScheduledOutreachStartAt,
    getFilledShiftIds,
    getOriginalCaregiverIdForShift,
    getScheduledOutreachState,
} from '@apps/web/lib/outreach_utils';
import {
    type DayOfWeek,
    type OrgCaregiverRecord,
    type OrgClientRead,
    type Outreach,
    type OutreachResponseCounts,
    type Shift,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

export type CalendarStatusBucket =
    | 'open'
    | 'scheduled'
    | 'pending_confirmation'
    | 'confirmed'
    | 'in_progress'
    | 'completed'
    | 'clock_issue'
    | 'attention'
    | 'cancelled';

export type CalendarChipBucket = CalendarStatusBucket | 'outreaching';

// Mirrors CalendarChipBucket for runtime (Zod) validation of persisted
// filter state; a stored bucket that is no longer valid fails validation.
export const CALENDAR_CHIP_BUCKET_VALUES = [
    'open',
    'scheduled',
    'pending_confirmation',
    'confirmed',
    'in_progress',
    'completed',
    'clock_issue',
    'attention',
    'cancelled',
    'outreaching',
] as const satisfies ReadonlyArray<CalendarChipBucket>;

export type CalendarGroupBy = 'client' | 'caregiver';

export const CALENDAR_GROUP_BY_VALUES = [
    'client',
    'caregiver',
] as const satisfies ReadonlyArray<CalendarGroupBy>;

export type CalendarChipPalette = {
    readonly label: string;
    // Highlighted buckets (active states) get a strong tint plus a bold
    // outline; quiet buckets (settled states) get a light tint.
    readonly tier: 'highlighted' | 'quiet';
    readonly chipClassName: string;
    readonly outlineClassName?: string;
    readonly dotClassName: string;
    // Text color for the popover's uppercase status eyebrow.
    readonly eyebrowClassName: string;
};

export type CalendarRow = {
    readonly key: string;
    readonly label: string;
    readonly sublabel: string | null;
    readonly isOpenRow: boolean;
    readonly shifts: ReadonlyArray<Shift>;
    readonly shiftsByDay: ReadonlyMap<string, ReadonlyArray<Shift>>;
};

export type ShiftOutreachStatus = {
    readonly activeFillOutreach: Outreach | null;
    // Response counts for activeFillOutreach; null until the batch overlay
    // returns counts (or when the caller didn't request them).
    readonly activeFillResponseCounts: OutreachResponseCounts | null;
    readonly latestTerminalFillOutreach: Outreach | null;
    readonly clockReminderOutreach: Outreach | null;
};

// Include/exclude selection for a filter category. Empty include means "no
// restriction"; exclude always wins over include for the same value.
export type CalendarFilterSelection = {
    readonly include: ReadonlySet<string>;
    readonly exclude: ReadonlySet<string>;
};

// Array form of a selection, used by component state and persistence where a
// Set isn't serializable/ergonomic.
export type CalendarSelectionValues = {
    readonly include: ReadonlyArray<string>;
    readonly exclude: ReadonlyArray<string>;
};

export type CalendarFilterState = {
    readonly buckets: ReadonlySet<CalendarChipBucket>;
    readonly offices: CalendarFilterSelection;
    readonly careCoordinators: CalendarFilterSelection;
    readonly tags: CalendarFilterSelection;
    readonly clientSearch: string;
    readonly showRecurringShifts: boolean;
    readonly showFillInShifts: boolean;
    // Cancelled shifts stay hidden unless the active buckets include cancelled.
    readonly revealCancelled: boolean;
};

export type CalendarShiftSelectionEligibility = {
    readonly isEligible: boolean;
    readonly disabledReason: string | null;
};

// Selection controls shared by every calendar view (week grid, month grid,
// day timeline); null when multi-shift outreach selection is disabled.
export type CalendarSelectionControls = {
    readonly selectedShiftIds: ReadonlySet<string>;
    readonly excludedShiftIds: ReadonlySet<string>;
    readonly maximumSelectedShifts: number;
    readonly onToggleShift: (shift: Shift) => void;
    readonly onToggleClientShifts: (shifts: ReadonlyArray<Shift>) => void;
    readonly onMarqueeSelection: (
        coveredShifts: ReadonlyArray<Shift>,
        baseSelectedIds: ReadonlySet<string>,
        isFinal: boolean,
    ) => void;
};

// Only triage-worthy buckets keep a distinct highlighted color: open (red),
// outreaching (purple), clock_issue/attention (orange). The settled buckets
// (scheduled/pending_confirmation/confirmed/in_progress) share one quiet
// neutral treatment — their specific state shows as in-card text — and
// completed/cancelled stay quiet grey. Dark mode inverts each tint.
const NEUTRAL_QUIET_CHIP_TREATMENT: Omit<CalendarChipPalette, 'label'> = {
    tier: 'quiet',
    chipClassName:
        'border-[#9EADB0] bg-[#DBE3E3] text-[#244045] dark:border-[#42595E] dark:bg-[#0D3036] dark:text-[#BDC9C9]',
    dotClassName: 'bg-[#617578] dark:bg-[#9EADB0]',
    eyebrowClassName: 'text-[#617578] dark:text-[#9EADB0]',
};

export const CALENDAR_CHIP_PALETTE: Record<
    CalendarChipBucket,
    CalendarChipPalette
> = {
    open: {
        label: 'Open',
        tier: 'highlighted',
        chipClassName:
            'border-[#FF3B57] bg-[#FFA8B2] text-[#420000] dark:border-[#A8002E] dark:bg-[#420000]/70 dark:text-[#FFF0F2]',
        outlineClassName: 'border-2 border-[#E0003D] dark:border-[#FF858F]',
        dotClassName: 'bg-[#E0003D] dark:bg-[#FF858F]',
        eyebrowClassName: 'text-[#E0003D] dark:text-[#FF858F]',
    },
    outreaching: {
        label: 'Outreaching',
        tier: 'highlighted',
        chipClassName:
            'border-[#8C82FF] bg-[#C4C2FF] text-[#1F125E] dark:border-[#5533EE] dark:bg-[#1F125E]/70 dark:text-[#F5F2FF]',
        outlineClassName: 'border-2 border-[#5533EE] dark:border-[#8C82FF]',
        dotClassName: 'bg-[#5533EE] dark:bg-[#8C82FF]',
        eyebrowClassName: 'text-[#5533EE] dark:text-[#8C82FF]',
    },
    scheduled: {
        label: 'Scheduled',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    pending_confirmation: {
        label: 'Pending confirmation',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    confirmed: {
        label: 'Confirmed',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    in_progress: {
        label: 'In progress',
        ...NEUTRAL_QUIET_CHIP_TREATMENT,
    },
    completed: {
        label: 'Completed',
        tier: 'quiet',
        chipClassName:
            'border-[#9EADB0] bg-[#DBE3E3] text-[#244045] dark:border-[#42595E] dark:bg-[#0D3036] dark:text-[#BDC9C9]',
        dotClassName: 'bg-[#617578] dark:bg-[#9EADB0]',
        eyebrowClassName: 'text-[#617578] dark:text-[#9EADB0]',
    },
    clock_issue: {
        label: 'Clock issue',
        tier: 'highlighted',
        chipClassName:
            'border-[#F05238] bg-[#FFAB96] text-[#380F00] dark:border-[#A31A0F] dark:bg-[#380F00]/70 dark:text-[#FFF0ED]',
        outlineClassName: 'border-2 border-[#D13621] dark:border-[#FF785C]',
        dotClassName: 'bg-[#D13621] dark:bg-[#FF785C]',
        eyebrowClassName: 'text-[#D13621] dark:text-[#FF785C]',
    },
    attention: {
        label: 'Needs attention',
        tier: 'highlighted',
        chipClassName:
            'border-[#F05238] bg-[#FFAB96] text-[#380F00] dark:border-[#A31A0F] dark:bg-[#380F00]/70 dark:text-[#FFF0ED]',
        outlineClassName: 'border-2 border-[#D13621] dark:border-[#FF785C]',
        dotClassName: 'bg-[#D13621] dark:bg-[#FF785C]',
        eyebrowClassName: 'text-[#D13621] dark:text-[#FF785C]',
    },
    cancelled: {
        label: 'Cancelled',
        tier: 'quiet',
        chipClassName:
            'border-[#9EADB0] bg-[#BDC9C9] text-[#42595E] dark:border-[#42595E] dark:bg-[#0D3036] dark:text-[#9EADB0]',
        dotClassName: 'bg-[#829494] dark:bg-[#9EADB0]',
        eyebrowClassName: 'text-[#829494] dark:text-[#9EADB0]',
    },
};

// A saved view is a named filter preset shown as a tab above the calendar.
// Views are fixed org-wide defaults for now (creating custom views is
// intentionally out of scope). Filter edits are cached in localStorage per
// user: office/care-coordinator selections are global and follow the user
// across every view, while status buckets, shift types, and hide-empty-rows
// are cached per view (client search stays session-local). Re-selecting the
// already-active tab resets everything to the view's saved configuration —
// see useCalendarViewFilterPersistence.
export type CalendarSavedView = {
    readonly id: string;
    readonly label: string;
    readonly buckets: ReadonlyArray<CalendarChipBucket>;
    readonly hideEmptyRows: boolean;
    readonly showRecurringShifts: boolean;
    readonly showFillInShifts: boolean;
};

export const DEFAULT_CALENDAR_VIEW: CalendarSavedView = {
    id: 'all',
    label: 'All shifts',
    buckets: [],
    hideEmptyRows: true,
    showRecurringShifts: true,
    showFillInShifts: true,
};

export const CALENDAR_SAVED_VIEWS: ReadonlyArray<CalendarSavedView> = [
    DEFAULT_CALENDAR_VIEW,
    {
        id: 'open',
        label: 'Open shifts',
        buckets: ['open', 'outreaching'],
        hideEmptyRows: true,
        showRecurringShifts: true,
        showFillInShifts: true,
    },
];

// Resolve a persisted or deep-linked view id. The retired Phoebe Outreach
// view folded into the open view, so its id resolves there; other unknown or
// stale ids (e.g. the removed attention/clock_issues/phoebe_working views)
// degrade to the default so old localStorage payloads never leave the
// calendar view-less.
export function resolveCalendarSavedView(
    id: string | null | undefined,
): CalendarSavedView {
    const resolvedId = id === 'phoebe_outreach' ? 'open' : id;
    return (
        CALENDAR_SAVED_VIEWS.find((view) => view.id === resolvedId) ??
        DEFAULT_CALENDAR_VIEW
    );
}

// Saved views always start with empty chips, offices, coordinators, tags, and
// search, so any of them being set counts as drift.
export function isCalendarViewModified(
    view: CalendarSavedView,
    state: {
        readonly buckets: ReadonlyArray<CalendarChipBucket>;
        readonly offices: CalendarSelectionValues;
        readonly careCoordinators: CalendarSelectionValues;
        readonly tags: CalendarSelectionValues;
        readonly clientSearch: string;
        readonly hideEmptyRows: boolean;
        readonly showRecurringShifts: boolean;
        readonly showFillInShifts: boolean;
    },
): boolean {
    return (
        state.buckets.length > 0 ||
        state.offices.include.length + state.offices.exclude.length > 0 ||
        state.careCoordinators.include.length +
            state.careCoordinators.exclude.length >
            0 ||
        state.tags.include.length + state.tags.exclude.length > 0 ||
        state.clientSearch.trim().length > 0 ||
        state.hideEmptyRows !== view.hideEmptyRows ||
        state.showRecurringShifts !== view.showRecurringShifts ||
        state.showFillInShifts !== view.showFillInShifts
    );
}

export function resolveCalendarStatusBucket(
    status: UnifiedShiftStatus,
): CalendarStatusBucket {
    switch (status) {
        case 'needs_coverage':
        case 'open':
            return 'open';
        case 'assigned':
            return 'scheduled';
        case 'pending_confirmation':
        case 'confirmation_follow_up':
            return 'pending_confirmation';
        case 'confirmed':
            return 'confirmed';
        case 'in_progress':
            return 'in_progress';
        case 'completed':
            return 'completed';
        case 'clock_in_reminder':
        case 'clock_out_reminder':
            return 'clock_issue';
        case 'declined':
        case 'missing':
        case 'needs_attention':
        case 'needs_review':
        case 'no_response':
        case 'not_scheduled':
        case 'unknown':
            return 'attention';
        case 'cancelled':
            return 'cancelled';
        default:
            return assert.never(status);
    }
}

export function getShiftCalendarStatusBucket(
    shift: Shift,
): CalendarStatusBucket {
    return resolveCalendarStatusBucket(
        resolveShiftStatus(shift.status, shift.latest_stage),
    );
}

export function classifyShiftOutreaches(
    shiftId: string,
    outreaches: ReadonlyArray<Outreach>,
    responseCountsByOutreachId?: Readonly<
        Record<string, OutreachResponseCounts>
    >,
): ShiftOutreachStatus {
    return classifyShiftOutreachList(
        outreaches.filter((outreach) => outreach.shift_ids.includes(shiftId)),
        responseCountsByOutreachId,
    );
}

export function buildShiftOutreachStatusMap(
    shifts: ReadonlyArray<Shift>,
    outreaches: ReadonlyArray<Outreach>,
    responseCountsByOutreachId?: Readonly<
        Record<string, OutreachResponseCounts>
    >,
): ReadonlyMap<string, ShiftOutreachStatus> {
    const outreachesByShiftId = new Map<string, Array<Outreach>>();
    for (const outreach of outreaches) {
        for (const shiftId of outreach.shift_ids) {
            const shiftOutreaches = outreachesByShiftId.get(shiftId);
            if (shiftOutreaches) {
                shiftOutreaches.push(outreach);
            } else {
                outreachesByShiftId.set(shiftId, [outreach]);
            }
        }
    }

    return new Map(
        shifts.map((shift) => [
            shift.id,
            classifyShiftOutreachList(
                outreachesByShiftId.get(shift.id) ?? [],
                responseCountsByOutreachId,
            ),
        ]),
    );
}

// Unified statuses whose source-of-truth shift row says the shift is filled
// (mirrors the backend's filled-family statuses in is_source_filled_shift).
const SOURCE_FILLED_UNIFIED_STATUSES: ReadonlySet<UnifiedShiftStatus> = new Set(
    ['assigned', 'pending_confirmation', 'confirmation_follow_up', 'confirmed'],
);

/**
 * Whether the EHR says this shift was filled without going through the given
 * fill outreach — i.e. it gained a caregiver elsewhere while the outreach was
 * already running. Only a started outreach (in progress or paused) counts: a
 * still-pending draft or scheduled callout on an assigned shift is a
 * deliberate setup (the caregiver called out for a future date), not a
 * conflict with EHR truth. And a callout starts with the shift still
 * assigned to the caregiver being replaced, so the EHR naming that same
 * original caregiver is the callout's starting state, not a fill — only a
 * different caregiver is evidence of an external fill.
 */
export function isShiftFilledOutsideActiveOutreach(
    shift: Shift,
    outreach: Outreach,
): boolean {
    if (outreach.status !== 'in_progress' && outreach.status !== 'paused') {
        return false;
    }
    if (getFilledShiftIds(outreach.outreach_metadata).has(shift.id)) {
        return false;
    }
    if (
        !SOURCE_FILLED_UNIFIED_STATUSES.has(
            resolveShiftStatus(shift.status, shift.latest_stage),
        )
    ) {
        return false;
    }
    const originalCaregiverId = getOriginalCaregiverIdForShift(
        outreach.outreach_metadata,
        shift.id,
    );
    if (originalCaregiverId === null) {
        // No original caregiver recorded: the shift went from open to
        // assigned while the outreach ran, which is itself an external fill.
        return true;
    }
    const currentCaregiverId =
        shift.assigned_caregiver_id ?? shift.caregiver?.id ?? null;
    return (
        currentCaregiverId !== null &&
        currentCaregiverId !== originalCaregiverId
    );
}

export function getCalendarDisplayBucket(
    shift: Shift,
    outreachStatus: ShiftOutreachStatus | undefined,
): CalendarChipBucket {
    const statusBucket = getShiftCalendarStatusBucket(shift);
    // Settled EHR reality never yields to outreach state: finished,
    // started, and clock-stage shifts keep their own bucket.
    if (
        statusBucket === 'cancelled' ||
        statusBucket === 'completed' ||
        statusBucket === 'in_progress' ||
        statusBucket === 'clock_issue'
    ) {
        return statusBucket;
    }

    const active = outreachStatus?.activeFillOutreach;
    if (!active || getFilledShiftIds(active.outreach_metadata).has(shift.id)) {
        return statusBucket;
    }
    // A shift assigned in the EHR while a started fill outreach still runs
    // was filled elsewhere — EHR truth outranks the outreach overlay.
    if (isShiftFilledOutsideActiveOutreach(shift, active)) {
        return statusBucket;
    }
    return 'outreaching';
}

export type CalendarDaySummary = {
    readonly totalShifts: number;
    readonly totalHours: number;
    readonly openCount: number;
    readonly openHours: number;
    readonly outreachingCount: number;
    // Assigned but not yet started: scheduled, pending confirmation, or
    // confirmed.
    readonly filledCount: number;
    readonly inProgressCount: number;
    readonly completedCount: number;
    // Clock issues plus needs-attention states.
    readonly issueCount: number;
    readonly cancelledCount: number;
};

/**
 * Per-day rollup backing the month view's stat cells and the day summary
 * panel header. Counts use display buckets, so a shift with an active fill
 * outreach counts as outreaching rather than open.
 */
export function summarizeCalendarDayShifts(
    shifts: ReadonlyArray<Shift>,
    outreachByShiftId: ReadonlyMap<string, ShiftOutreachStatus>,
): CalendarDaySummary {
    let openCount = 0;
    const openShifts: Array<Shift> = [];
    let outreachingCount = 0;
    let filledCount = 0;
    let inProgressCount = 0;
    let completedCount = 0;
    let issueCount = 0;
    let cancelledCount = 0;

    for (const shift of shifts) {
        const bucket = getCalendarDisplayBucket(
            shift,
            outreachByShiftId.get(shift.id),
        );
        switch (bucket) {
            case 'open':
                openCount += 1;
                openShifts.push(shift);
                break;
            case 'outreaching':
                outreachingCount += 1;
                break;
            case 'scheduled':
            case 'pending_confirmation':
            case 'confirmed':
                filledCount += 1;
                break;
            case 'in_progress':
                inProgressCount += 1;
                break;
            case 'completed':
                completedCount += 1;
                break;
            case 'clock_issue':
            case 'attention':
                issueCount += 1;
                break;
            case 'cancelled':
                cancelledCount += 1;
                break;
            default:
                assert.never(bucket);
        }
    }

    return {
        totalShifts: shifts.length,
        totalHours: getWeeklyShiftHours(shifts),
        openCount,
        openHours: getWeeklyShiftHours(openShifts),
        outreachingCount,
        filledCount,
        inProgressCount,
        completedCount,
        issueCount,
        cancelledCount,
    };
}

export function getShiftChipCaregiverLabel(
    shift: Shift,
    displayBucket: CalendarChipBucket,
): string | null {
    if (shift.caregiver?.name) {
        return getCaregiverDisplayName(shift.caregiver.name);
    }
    if (displayBucket === 'open') {
        return 'Open shift';
    }
    // An outreaching shift is being worked, so it skips act-on-this words
    // like "Open"/"Unassigned" — its outreach progress line says it all.
    if (displayBucket === 'outreaching') {
        return null;
    }
    return 'Unassigned';
}

/**
 * Number of shifts the active fill outreach spans, or null when the shift
 * isn't part of a multi-shift outreach. This is the chip-level "grouped"
 * signal; the popover's group summary refines it with joined-outreach data.
 */
export function getShiftGroupSize(
    outreachStatus: ShiftOutreachStatus | undefined,
): number | null {
    const size = outreachStatus?.activeFillOutreach?.shift_ids.length ?? 0;
    return size > 1 ? size : null;
}

/**
 * Human-readable recurrence for a shift ("Repeats weekly", "Repeats every
 * 2 weeks"), or null for one-time shifts. A frequency value this bundle
 * doesn't recognize (a newer server can add one mid-rollout) degrades to a
 * generic "Repeats" rather than hiding that the shift recurs.
 */
export function formatShiftRecurrenceLabel(
    shift: Pick<Shift, 'frequency' | 'recurrence_interval'>,
): string | null {
    // Missing, fractional, or non-positive intervals read as the base
    // cadence instead of producing "Repeats every 0 weeks".
    const interval =
        typeof shift.recurrence_interval === 'number' &&
        Number.isInteger(shift.recurrence_interval) &&
        shift.recurrence_interval > 1
            ? shift.recurrence_interval
            : 1;
    switch (shift.frequency) {
        case 'once':
            return null;
        case 'daily':
            return interval === 1
                ? 'Repeats daily'
                : `Repeats every ${interval} days`;
        case 'weekly':
            return interval === 1
                ? 'Repeats weekly'
                : `Repeats every ${interval} weeks`;
        case 'monthly':
            return interval === 1
                ? 'Repeats monthly'
                : `Repeats every ${interval} months`;
        default:
            return 'Repeats';
    }
}

export type ShiftChipOutreachStage =
    | 'draft'
    | 'scheduled'
    | 'paused'
    | 'waiting'
    | 'contacted'
    | 'needs_review'
    | 'accepted'
    | 'exhausted';

// The rail is the chip's single state instrument: `live` animates while the
// outreach is running (fraction = responses over contacted), `nub` marks
// pre-start work that exists but hasn't progressed, `tone` colors the whole
// rail to say why the card needs a person (good news vs fill at risk).
export type ShiftChipOutreachRail =
    | { readonly kind: 'live'; readonly fraction: number }
    | { readonly kind: 'nub' }
    | { readonly kind: 'tone'; readonly tone: 'good' | 'risk' };

// Top-right ledger stat on the chip's time line: the trigger that explains
// the card ("5/12" responded, "4p" send time, "1 yes", "1 new", "7/12 no",
// "4h" to shift start). `mutedText` renders dimmed ("/12"); `suffixText`
// renders in the stat tone after a space ("no").
export type ShiftChipOutreachStat = {
    readonly tone: 'progress' | 'muted' | 'good' | 'risk';
    readonly text: string;
    readonly mutedText: string | null;
    readonly suffixText: string | null;
};

export type ShiftChipOutreachCard = {
    readonly stage: ShiftChipOutreachStage;
    // calm: Phoebe holds the work (lavender material, progress rail).
    // needs_you: a person must act (elevated card, action row, tone rail).
    readonly variant: 'calm' | 'needs_you';
    readonly rail: ShiftChipOutreachRail;
    // Status phrase for the popover eyebrow and chip aria label.
    readonly label: string;
    readonly stat: ShiftChipOutreachStat | null;
    // Second line on calm cards: the richest fact that fits ("4 no · 8
    // waiting", "Draft").
    readonly factLine: string | null;
    // Second line on needs_you cards: action verb rendered as a list row
    // with a trailing chevron — the chip's affordance for the popover's
    // primary action, not a separate button.
    readonly actionVerb: string | null;
};

type OutreachProgressSource = Pick<Outreach, 'status' | 'outreach_metadata'>;

/**
 * Per-state card model for an active outreaching chip. Every card shares one
 * lavender material; state shows in the rail (purple live/nub while Phoebe
 * works, green/coral tone when a person must act) and in the tone-colored
 * action row. Precedence for a running outreach: acceptances beat replies
 * needing review, which beat an exhausted pool; paused always wins. Terminal
 * statuses should not reach here (the shift falls back to its own status)
 * but resolve safely to a generic outreaching card.
 */
export function getShiftChipOutreachCard({
    outreach,
    counts,
    shift,
    timezone,
    now,
}: {
    readonly outreach: OutreachProgressSource;
    readonly counts: OutreachResponseCounts | null;
    readonly shift: Shift;
    readonly timezone: string;
    readonly now: Date;
}): ShiftChipOutreachCard {
    switch (outreach.status) {
        case 'pending': {
            // Scheduling is a metadata projection on a still-pending outreach;
            // match the callout badge's vocabulary (Draft/Scheduled/Paused).
            const sendsAt = getEffectiveScheduledOutreachStartAt(
                outreach.outreach_metadata,
            );
            if (sendsAt === null) {
                return calmOutreachCard('draft', 'Draft', 'Draft', null);
            }
            if (
                getScheduledOutreachState(outreach.outreach_metadata) ===
                'paused'
            ) {
                return pausedOutreachCard(shift, now);
            }
            // Prefix the weekday when the send lands on another day so a
            // bare "4p" never reads as today.
            const sendTime =
                toDateKey(new Date(sendsAt), timezone) ===
                toDateKey(now, timezone)
                    ? formatCompactTime(sendsAt, timezone)
                    : `${formatInTimeZone(new Date(sendsAt), timezone, 'EEE')} ${formatCompactTime(sendsAt, timezone)}`;
            return calmOutreachCard(
                'scheduled',
                `Draft · sends ${sendTime}`,
                'Draft',
                {
                    tone: 'muted',
                    text: sendTime,
                    mutedText: null,
                    suffixText: null,
                },
            );
        }
        case 'paused':
            return pausedOutreachCard(shift, now);
        case 'in_progress':
            return runningOutreachCard(counts, shift.id);
        case 'filled':
        case 'unfilled':
        case 'cancelled':
            return calmOutreachCard(
                'waiting',
                'Outreaching',
                'Outreaching',
                null,
            );
        default:
            return assert.never(outreach.status);
    }
}

// At least half the contacted pool declined: the fill is at risk and waiting
// longer won't help, so the card asks for more caregivers.
const EXHAUSTED_POOL_DECLINED_FRACTION = 0.5;
// Floor so one early "no" in a small pool doesn't flip a fresh outreach
// straight into an alarmed Add-caregivers card.
const EXHAUSTED_POOL_MINIMUM_DECLINED = 3;

function runningOutreachCard(
    counts: OutreachResponseCounts | null,
    shiftId: string,
): ShiftChipOutreachCard {
    const contacted = counts?.contacted_count ?? 0;
    const accepted = counts?.accepted_count ?? 0;
    const declined = counts?.declined_count ?? 0;
    const needsReview = counts?.needs_review_count ?? 0;
    // The chip claims a yes only when someone accepted this shift; on a
    // multi-shift outreach a caregiver available only for a sibling shift
    // must not flip this chip to "Assign & fill".
    const acceptedForShift = getAcceptedCountForShift(counts, shiftId);
    if (acceptedForShift > 0) {
        // Name-first when the payload carries names ("Maria G. +1 said
        // yes"): the action row names who to assign, and the chevron keeps
        // the popover affordance. Count-only payloads keep the generic
        // Assign & fill verb.
        const acceptedNames = formatAcceptedCaregiverNames({
            counts,
            shiftId,
            maxNames: 1,
            compact: true,
        });
        return {
            stage: 'accepted',
            variant: 'needs_you',
            rail: { kind: 'tone', tone: 'good' },
            label:
                acceptedNames === null
                    ? `${acceptedForShift} said yes`
                    : `${acceptedNames} said yes`,
            stat: {
                tone: 'good',
                text: `${acceptedForShift} yes`,
                mutedText: null,
                suffixText: null,
            },
            factLine: null,
            actionVerb: 'Assign & fill',
        };
    }
    if (needsReview > 0) {
        return {
            stage: 'needs_review',
            variant: 'needs_you',
            rail: { kind: 'tone', tone: 'risk' },
            label:
                needsReview === 1
                    ? '1 reply needs review'
                    : `${needsReview} replies need review`,
            stat: {
                tone: 'risk',
                text: `${needsReview} new`,
                mutedText: null,
                suffixText: null,
            },
            factLine: null,
            actionVerb: 'Review reply',
        };
    }
    if (
        declined >= EXHAUSTED_POOL_MINIMUM_DECLINED &&
        declined >= contacted * EXHAUSTED_POOL_DECLINED_FRACTION
    ) {
        return {
            stage: 'exhausted',
            variant: 'needs_you',
            rail: { kind: 'tone', tone: 'risk' },
            label: `${declined} of ${contacted} unavailable`,
            stat: {
                tone: 'risk',
                text: `${declined}`,
                mutedText: `/${contacted}`,
                suffixText: 'no',
            },
            factLine: null,
            actionVerb: 'Add caregivers',
        };
    }
    // A calm running card only exists with zero yes/review replies, so any
    // tally would just restate the responded fraction — the stat carries the
    // numbers and line two stays a quiet state word.
    const responded = accepted + declined + needsReview;
    return {
        stage: contacted > 0 ? 'contacted' : 'waiting',
        variant: 'calm',
        rail: {
            kind: 'live',
            fraction: contacted > 0 ? responded / contacted : 0,
        },
        label: 'Outreaching',
        stat:
            contacted > 0
                ? {
                      tone: 'progress',
                      text: `${responded}`,
                      mutedText: `/${contacted}`,
                      suffixText: null,
                  }
                : null,
        factLine: 'Outreaching',
        actionVerb: null,
    };
}

function pausedOutreachCard(shift: Shift, now: Date): ShiftChipOutreachCard {
    const startMs = new Date(shift.shift_start_time).getTime() - now.getTime();
    return {
        stage: 'paused',
        variant: 'needs_you',
        rail: { kind: 'tone', tone: 'risk' },
        label: 'Paused',
        // Time until the shift starts is the urgency trigger for resuming.
        stat:
            startMs > 0
                ? {
                      tone: 'risk',
                      text: formatShortDuration(startMs),
                      mutedText: null,
                      suffixText: null,
                  }
                : null,
        factLine: null,
        actionVerb: 'Resume',
    };
}

function calmOutreachCard(
    stage: ShiftChipOutreachStage,
    label: string,
    factLine: string,
    stat: ShiftChipOutreachStat | null,
): ShiftChipOutreachCard {
    return {
        stage,
        variant: 'calm',
        rail: { kind: 'nub' },
        label,
        stat,
        factLine,
        actionVerb: null,
    };
}

function formatShortDuration(durationMs: number): string {
    const minutes = Math.floor(durationMs / 60_000);
    if (minutes < 60) {
        return `${Math.max(minutes, 1)}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${hours}h`;
    }
    return `${Math.floor(hours / 24)}d`;
}

export function getCalendarShiftSelectionEligibility(
    shift: Shift,
    outreachStatus: ShiftOutreachStatus | undefined,
    isSelected: boolean,
    isAtSelectionCap: boolean,
    maximumSelectedShifts: number,
): CalendarShiftSelectionEligibility {
    const displayBucket = getCalendarDisplayBucket(shift, outreachStatus);
    if (displayBucket === 'outreaching') {
        return {
            isEligible: false,
            disabledReason: 'Already has an active outreach',
        };
    }
    if (displayBucket !== 'open') {
        return {
            isEligible: false,
            disabledReason: 'Outreaches can only be created for open shifts',
        };
    }
    if (!isSelected && isAtSelectionCap) {
        return {
            isEligible: false,
            disabledReason: `Maximum ${maximumSelectedShifts} shifts per outreach`,
        };
    }

    return { isEligible: true, disabledReason: null };
}

/**
 * Shifts targeted by the row-level "select all open shifts" control: open
 * shifts without an active fill outreach that are eligible for selection.
 */
export function filterOpenShiftsForOutreach(
    shifts: ReadonlyArray<Shift>,
    outreachByShiftId: ReadonlyMap<string, ShiftOutreachStatus>,
): Array<Shift> {
    return shifts.filter((shift) => {
        const outreachStatus = outreachByShiftId.get(shift.id);
        return (
            getCalendarDisplayBucket(shift, outreachStatus) === 'open' &&
            getCalendarShiftSelectionEligibility(
                shift,
                outreachStatus,
                true,
                false,
                0,
            ).isEligible
        );
    });
}

export function pruneIneligibleSelections(
    selectedShiftIds: ReadonlySet<string>,
    shifts: ReadonlyArray<Shift>,
    outreachByShiftId: ReadonlyMap<string, ShiftOutreachStatus>,
): ReadonlySet<string> {
    const shiftsById = new Map(shifts.map((shift) => [shift.id, shift]));
    let prunedShiftIds: Set<string> | null = null;

    for (const shiftId of selectedShiftIds) {
        const shift = shiftsById.get(shiftId);
        if (!shift) {
            continue;
        }

        const eligibility = getCalendarShiftSelectionEligibility(
            shift,
            outreachByShiftId.get(shiftId),
            true,
            false,
            selectedShiftIds.size,
        );
        if (!eligibility.isEligible) {
            prunedShiftIds ??= new Set(selectedShiftIds);
            prunedShiftIds.delete(shiftId);
        }
    }

    return prunedShiftIds ?? selectedShiftIds;
}

export type CalendarMarqueeSelectionResult = {
    readonly selectedShiftIds: ReadonlySet<string>;
    readonly excludedShiftIds: ReadonlySet<string>;
};

// Resolve a marquee (rubber-band) drag into the next selection: the base
// selection (previous selection on shift/cmd-drag, empty otherwise) is always
// retained, newly covered shifts are added in the order given until the cap,
// and the overflow is reported so the UI can show which shifts were excluded.
// Callers pass `coveredShifts` in priority order — the grid orders them by
// when the drag rectangle first hit each shift, tie-breaking left-to-right.
export function resolveCalendarMarqueeSelection(
    baseSelectedIds: ReadonlySet<string>,
    coveredShifts: ReadonlyArray<Shift>,
    maximumSelectedShifts: number,
): CalendarMarqueeSelectionResult {
    const selectedShiftIds = new Set(baseSelectedIds);
    const excludedShiftIds = new Set<string>();
    const additions = coveredShifts.filter(
        (shift) => !selectedShiftIds.has(shift.id),
    );

    for (const shift of additions) {
        if (selectedShiftIds.size < maximumSelectedShifts) {
            selectedShiftIds.add(shift.id);
        } else {
            excludedShiftIds.add(shift.id);
        }
    }

    return { selectedShiftIds, excludedShiftIds };
}

export function orderSelectedShiftIdsByStartTime(
    selectedShiftIds: ReadonlySet<string>,
    shifts: ReadonlyArray<Shift>,
): Array<string> {
    const shiftsById = new Map(shifts.map((shift) => [shift.id, shift]));

    return Array.from(selectedShiftIds).sort((leftId, rightId) => {
        const leftShift = shiftsById.get(leftId);
        const rightShift = shiftsById.get(rightId);
        if (leftShift && rightShift) {
            const startTimeComparison =
                leftShift.shift_start_time.localeCompare(
                    rightShift.shift_start_time,
                );
            return startTimeComparison || leftId.localeCompare(rightId);
        }
        if (leftShift) {
            return -1;
        }
        if (rightShift) {
            return 1;
        }
        return leftId.localeCompare(rightId);
    });
}

function matchesFilterSelection(
    values: ReadonlyArray<string>,
    selection: CalendarFilterSelection,
): boolean {
    if (
        selection.exclude.size > 0 &&
        values.some((value) => selection.exclude.has(value))
    ) {
        return false;
    }
    if (selection.include.size === 0) {
        return true;
    }
    return values.some((value) => selection.include.has(value));
}

export function isRenderableCalendarShift(
    shift: Shift,
    outreachStatus: ShiftOutreachStatus | undefined,
    filters: CalendarFilterState,
): boolean {
    const isRecurring = shift.frequency !== 'once';
    if (
        (isRecurring && !filters.showRecurringShifts) ||
        (!isRecurring && !filters.showFillInShifts)
    ) {
        return false;
    }

    const displayBucket = getCalendarDisplayBucket(shift, outreachStatus);
    if (displayBucket === 'cancelled' && !filters.revealCancelled) {
        return false;
    }

    const matchesBucket =
        filters.buckets.size === 0 || filters.buckets.has(displayBucket);
    const matchesOffice = matchesFilterSelection(
        [shift.patient_office_name ?? ''],
        filters.offices,
    );
    // Shifts with no coordinators match the "Unassigned" sentinel option.
    const coordinatorValues =
        shift.care_coordinators && shift.care_coordinators.length > 0
            ? shift.care_coordinators.map((coordinator) => coordinator.id)
            : [UNASSIGNED_CARE_COORDINATOR_ID];
    const matchesCareCoordinator = matchesFilterSelection(
        coordinatorValues,
        filters.careCoordinators,
    );
    const matchesTag = matchesFilterSelection(
        shift.patient_tags ?? [],
        filters.tags,
    );
    const normalizedSearch = filters.clientSearch.trim().toLocaleLowerCase();
    const matchesClient =
        normalizedSearch.length === 0 ||
        shift.patient_name.toLocaleLowerCase().includes(normalizedSearch);

    return (
        matchesBucket &&
        matchesOffice &&
        matchesCareCoordinator &&
        matchesTag &&
        matchesClient
    );
}

export type CalendarRowFilterResult = {
    // Whether the row's label matches the current search text (or the search
    // is empty). A matching label reveals every shift in the row.
    readonly labelMatches: boolean;
    readonly shifts: ReadonlyArray<Shift>;
};

/**
 * Applies the pane's filters to one calendar row's shifts, search-aware at
 * the row level: a search hit on the row label reveals the row's shifts
 * regardless of their client names, while a miss falls back to per-shift
 * client-name matching (so caregiver rows still surface shifts for a
 * searched client). Week-grid rendering, the filtered-shift count, and the
 * empty-row search probe must all share this logic.
 */
export function filterCalendarRowShifts(
    row: CalendarRow,
    filters: CalendarFilterState,
    outreachByShiftId: ReadonlyMap<string, ShiftOutreachStatus>,
): CalendarRowFilterResult {
    const normalizedSearch = filters.clientSearch.trim().toLocaleLowerCase();
    const labelMatches =
        normalizedSearch.length === 0 ||
        row.label.toLocaleLowerCase().includes(normalizedSearch);
    const shiftFilters = {
        ...filters,
        clientSearch: labelMatches ? '' : filters.clientSearch,
    };
    return {
        labelMatches,
        shifts: row.shifts.filter((shift) =>
            isRenderableCalendarShift(
                shift,
                outreachByShiftId.get(shift.id),
                shiftFilters,
            ),
        ),
    };
}

export function normalizeCalendarWeekStartKey(
    from: string | undefined,
    timezone: string,
    weekStartDay: DayOfWeek = 'monday',
    referenceDate: Date = new Date(),
): string {
    const referenceKey = toDateKey(referenceDate, timezone);
    const anchorKey = isValidDateKey(from) ? from : referenceKey;
    const anchorDate = parseDateKey(anchorKey);
    const weekStartOffset =
        (anchorDate.getUTCDay() - WEEKDAY_INDEX_BY_DAY[weekStartDay] + 7) % 7;

    return shiftDateKey(anchorKey, -weekStartOffset);
}

export function getCalendarWeekDayKeys(
    weekStartKey: string,
): ReadonlyArray<string> {
    return Array.from({ length: 7 }, (_, index) =>
        shiftDateKey(weekStartKey, index),
    );
}

export type CalendarViewMode = 'day' | 'week' | 'month';

export const CALENDAR_VIEW_MODE_LABELS: Record<CalendarViewMode, string> = {
    day: 'Day',
    week: 'Week',
    month: 'Month',
};

export function normalizeCalendarDayKey(
    from: string | undefined,
    timezone: string,
    referenceDate: Date = new Date(),
): string {
    return isValidDateKey(from) ? from : toDateKey(referenceDate, timezone);
}

/** First day of the month containing `from` (or today when invalid). */
export function normalizeCalendarMonthStartKey(
    from: string | undefined,
    timezone: string,
    referenceDate: Date = new Date(),
): string {
    return startOfMonthDateKey(
        normalizeCalendarDayKey(from, timezone, referenceDate),
    );
}

/**
 * Day keys for the full month grid: whole weeks (per the org's week start
 * day) from the week containing the 1st through the week containing the
 * last day of the month. Always a multiple of 7 (28-42 days).
 */
export function getCalendarMonthGridDayKeys(
    monthStartKey: string,
    weekStartDay: DayOfWeek = 'monday',
): ReadonlyArray<string> {
    const gridStartKey = normalizeCalendarWeekStartKey(
        monthStartKey,
        'UTC',
        weekStartDay,
    );
    const monthEndKey = endOfMonthDateKey(monthStartKey);
    const dayKeys: Array<string> = [];
    for (
        let dayKey = gridStartKey;
        dayKey <= monthEndKey || dayKeys.length % 7 !== 0;
        dayKey = shiftDateKey(dayKey, 1)
    ) {
        dayKeys.push(dayKey);
    }
    return dayKeys;
}

// URL-backed person selection shared by every calendar time view; null means
// Everyone on the active client/caregiver axis.
export type CalendarMonthScope = {
    readonly kind: 'caregiver' | 'client';
    readonly ids: ReadonlyArray<string>;
};

const MONTH_SCOPE_KIND_PATTERN = /^(caregiver|client):(.+)$/iu;
const MONTH_SCOPE_ID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/**
 * Parses the `scope` URL parameter (`caregiver:<uuid>,<uuid>` /
 * `client:<uuid>,<uuid>`). Invalid IDs are ignored, and a value with no
 * valid IDs resolves to null (Everyone), so stale links cannot blank the
 * calendar.
 */
export function parseCalendarMonthScope(
    value: string | undefined,
): CalendarMonthScope | null {
    const match =
        value === undefined ? null : MONTH_SCOPE_KIND_PATTERN.exec(value);
    if (!match || !match[1] || !match[2]) {
        return null;
    }
    // The pattern is case-insensitive, so normalize the kind too — a
    // hand-edited `Caregiver:` must not silently parse as a client scope.
    const ids = Array.from(
        new Set(
            match[2]
                .split(',')
                .filter((id) => MONTH_SCOPE_ID_PATTERN.test(id))
                .map((id) => id.toLowerCase()),
        ),
    );
    if (ids.length === 0) {
        return null;
    }
    return {
        kind: match[1].toLowerCase() === 'caregiver' ? 'caregiver' : 'client',
        ids,
    };
}

export function serializeCalendarMonthScope(scope: CalendarMonthScope): string {
    return `${scope.kind}:${scope.ids.join(',')}`;
}

/**
 * Whether a shift belongs to the scoped entity. Caregiver scope covers the
 * shifts assigned to that caregiver; client scope covers every one of the
 * client's shifts, including open and outreaching shifts with no caregiver.
 */
export function shiftMatchesCalendarMonthScope(
    shift: Shift,
    scope: CalendarMonthScope,
): boolean {
    const entityId =
        scope.kind === 'caregiver'
            ? (shift.caregiver?.id ?? shift.assigned_caregiver_id)
            : shift.client_id;
    if (entityId === null || entityId === undefined) {
        return false;
    }
    return scope.ids.includes(entityId);
}

/**
 * Client month view measures all scheduled care, including open shifts.
 * Caregiver month view measures assigned workload, so open care and shifts
 * without a caregiver do not contribute to its cells or weekly totals.
 */
export function filterCalendarMonthShiftsForGroup(
    shifts: ReadonlyArray<Shift>,
    groupBy: CalendarGroupBy,
): ReadonlyArray<Shift> {
    if (groupBy === 'client') {
        return shifts;
    }
    return shifts.filter(
        (shift) =>
            getShiftCalendarStatusBucket(shift) !== 'open' &&
            Boolean(shift.caregiver?.id ?? shift.assigned_caregiver_id),
    );
}

/**
 * Availability gate for a deep-linked scope. The scope stays applied while
 * its roster is still loading (`rosterIds === null`) so restoring a URL never
 * flashes the unscoped month; once the roster has loaded, an entity found in
 * neither the roster nor the fetched shifts is dropped.
 */
export function resolveCalendarMonthScope(
    requested: CalendarMonthScope | null,
    rosterIds: ReadonlySet<string> | null,
    shifts: ReadonlyArray<Shift>,
): CalendarMonthScope | null {
    if (requested === null || rosterIds === null) {
        return requested;
    }
    const shiftEntityIds = new Set<string>();
    shifts.forEach((shift) => {
        const entityId =
            requested.kind === 'caregiver'
                ? (shift.caregiver?.id ?? shift.assigned_caregiver_id)
                : shift.client_id;
        if (entityId !== null && entityId !== undefined) {
            shiftEntityIds.add(entityId);
        }
    });
    const ids = requested.ids.filter((id) => {
        if (rosterIds.has(id)) {
            return true;
        }
        // Rosters are capped (caregivers) or active-only (clients); an entity
        // beyond that cap whose shifts are on the calendar is still scopeable.
        return shiftEntityIds.has(id);
    });
    return ids.length > 0 ? { ...requested, ids } : null;
}

/**
 * Whether a URL scope should be cleared after the roster and current range
 * have settled. An axis-mismatched scope is unavailable even when its IDs
 * are valid.
 */
export function shouldClearCalendarMonthScope(
    requested: CalendarMonthScope | null,
    resolved: CalendarMonthScope | null,
    groupBy: CalendarGroupBy,
    rosterIds: ReadonlySet<string> | null,
    isRangeLoading: boolean,
): boolean {
    const axisResolved = resolved?.kind === groupBy ? resolved : null;
    return (
        requested !== null &&
        axisResolved === null &&
        rosterIds !== null &&
        !isRangeLoading
    );
}

export function shouldRewriteCalendarMonthScope(
    requested: CalendarMonthScope | null,
    resolved: CalendarMonthScope | null,
    rosterIds: ReadonlySet<string> | null,
    isRangeLoading: boolean,
): boolean {
    return (
        requested !== null &&
        resolved !== null &&
        rosterIds !== null &&
        !isRangeLoading &&
        serializeCalendarMonthScope(requested) !==
            serializeCalendarMonthScope(resolved)
    );
}

export type CalendarTimelineLanes = {
    readonly laneByShiftId: ReadonlyMap<string, number>;
    readonly laneCount: number;
};

/**
 * Greedy interval packing of overlapping shifts into stacked sub-lanes so a
 * timeline row grows only as tall as its worst overlap.
 */
export function packShiftsIntoTimelineLanes(
    shifts: ReadonlyArray<Shift>,
): CalendarTimelineLanes {
    const sorted = [...shifts].sort(
        (left, right) =>
            left.shift_start_time.localeCompare(right.shift_start_time) ||
            left.shift_end_time.localeCompare(right.shift_end_time) ||
            left.id.localeCompare(right.id),
    );
    const laneEndTimes: Array<number> = [];
    const laneByShiftId = new Map<string, number>();

    for (const shift of sorted) {
        const startMs = new Date(shift.shift_start_time).getTime();
        const endMs = new Date(shift.shift_end_time).getTime();
        let laneIndex = laneEndTimes.findIndex(
            (laneEndMs) => laneEndMs <= startMs,
        );
        if (laneIndex === -1) {
            laneIndex = laneEndTimes.length;
            laneEndTimes.push(0);
        }
        laneEndTimes[laneIndex] = Math.max(endMs, startMs);
        laneByShiftId.set(shift.id, laneIndex);
    }

    return { laneByShiftId, laneCount: Math.max(1, laneEndTimes.length) };
}

/**
 * Wall-clock minutes of `iso` relative to midnight of `dayKey` in the org
 * timezone. Instants on a neighboring calendar day offset by ±1440 so a
 * shift running past midnight keeps a positive width when clamped.
 */
export function getWallClockMinutesForDay(
    iso: string,
    dayKey: string,
    timezone: string,
): number {
    const date = new Date(iso);
    const [hours, minutes] = formatInTimeZone(date, timezone, 'HH:mm')
        .split(':')
        .map(Number);
    const dayDifference = Math.round(
        (parseDateKey(toDateKey(date, timezone)).getTime() -
            parseDateKey(dayKey).getTime()) /
            86_400_000,
    );
    return dayDifference * 1440 + (hours ?? 0) * 60 + (minutes ?? 0);
}

export type CalendarTimelineWindow = {
    readonly startMinute: number;
    readonly endMinute: number;
};

const DEFAULT_TIMELINE_START_MINUTE = 6 * 60;
const DEFAULT_TIMELINE_END_MINUTE = 22 * 60;

/**
 * Visible hour window for the day timeline: 6a-10p by default, expanded to
 * whole hours so every shift on the day fits, and clamped to the day.
 */
export function getDayTimelineWindow(
    shifts: ReadonlyArray<Shift>,
    dayKey: string,
    timezone: string,
): CalendarTimelineWindow {
    let startMinute = DEFAULT_TIMELINE_START_MINUTE;
    let endMinute = DEFAULT_TIMELINE_END_MINUTE;

    for (const shift of shifts) {
        startMinute = Math.min(
            startMinute,
            getWallClockMinutesForDay(shift.shift_start_time, dayKey, timezone),
        );
        endMinute = Math.max(
            endMinute,
            getWallClockMinutesForDay(shift.shift_end_time, dayKey, timezone),
        );
    }

    return {
        startMinute: Math.max(0, Math.floor(startMinute / 60) * 60),
        endMinute: Math.min(1440, Math.ceil(endMinute / 60) * 60),
    };
}

export type CalendarNavigationPosition = {
    readonly rowKey: string;
    readonly dayKey: string;
    readonly shiftId: string;
};

export type CalendarNavigationMatrixRow = {
    readonly rowKey: string;
    readonly orderedShiftIds: ReadonlyArray<string>;
    readonly shiftIdsByDay: ReadonlyMap<string, ReadonlyArray<string>>;
    readonly dayKeyByShiftId: ReadonlyMap<string, string>;
};

export type CalendarNavigationMatrix = {
    readonly rows: ReadonlyArray<CalendarNavigationMatrixRow>;
    readonly rowIndexByKey: ReadonlyMap<string, number>;
};

export type CalendarNavigationRowInput = {
    readonly row: CalendarRow;
    readonly shifts: ReadonlyArray<Shift>;
};

// Ordered row/day/chip model backing keyboard roving focus, so navigation
// no longer scans the DOM. Only rows with rendered chips are included.
export function buildCalendarNavigationMatrix(
    visibleRows: ReadonlyArray<CalendarNavigationRowInput>,
    weekDayKeys: ReadonlyArray<string>,
): CalendarNavigationMatrix {
    const rows: Array<CalendarNavigationMatrixRow> = [];
    const rowIndexByKey = new Map<string, number>();

    function appendRow(
        rowKey: string,
        shiftsByDay: ReadonlyMap<string, ReadonlyArray<Shift>>,
    ): void {
        const orderedShiftIds: Array<string> = [];
        const shiftIdsByDay = new Map<string, ReadonlyArray<string>>();
        const dayKeyByShiftId = new Map<string, string>();

        for (const dayKey of weekDayKeys) {
            const dayShifts = shiftsByDay.get(dayKey);
            if (!dayShifts || dayShifts.length === 0) {
                continue;
            }

            const dayShiftIds = dayShifts.map((shift) => shift.id);
            shiftIdsByDay.set(dayKey, dayShiftIds);
            for (const shiftId of dayShiftIds) {
                orderedShiftIds.push(shiftId);
                dayKeyByShiftId.set(shiftId, dayKey);
            }
        }

        if (orderedShiftIds.length === 0) {
            return;
        }

        rowIndexByKey.set(rowKey, rows.length);
        rows.push({ rowKey, orderedShiftIds, shiftIdsByDay, dayKeyByShiftId });
    }

    for (const { row, shifts } of visibleRows) {
        appendRow(row.key, filterVisibleShiftsByDay(row.shiftsByDay, shifts));
    }

    return { rows, rowIndexByKey };
}

export function getCalendarNavigationTarget(
    matrix: CalendarNavigationMatrix,
    current: CalendarNavigationPosition,
    key: string,
    weekDayKeys: ReadonlyArray<string>,
): CalendarNavigationPosition | null {
    const rowIndex = matrix.rowIndexByKey.get(current.rowKey);
    if (rowIndex === undefined) {
        return null;
    }
    const row = matrix.rows[rowIndex];
    if (!row) {
        return null;
    }

    if (key === 'Home') {
        return positionInRow(row, row.orderedShiftIds[0]);
    }
    if (key === 'End') {
        return positionInRow(
            row,
            row.orderedShiftIds[row.orderedShiftIds.length - 1],
        );
    }
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
        const currentIndex = row.orderedShiftIds.indexOf(current.shiftId);
        if (currentIndex === -1) {
            return null;
        }
        const nextIndex =
            key === 'ArrowLeft' ? currentIndex - 1 : currentIndex + 1;
        return positionInRow(row, row.orderedShiftIds[nextIndex]);
    }
    if (key !== 'ArrowUp' && key !== 'ArrowDown') {
        return null;
    }

    const targetRow = matrix.rows[rowIndex + (key === 'ArrowDown' ? 1 : -1)];
    if (!targetRow) {
        return null;
    }
    const currentDayShiftIds = row.shiftIdsByDay.get(current.dayKey) ?? [];
    const currentDayIndex = currentDayShiftIds.indexOf(current.shiftId);
    const targetDayShiftIds = getNearestDayShiftIds(
        targetRow,
        current.dayKey,
        weekDayKeys,
    );
    if (!targetDayShiftIds || targetDayShiftIds.length === 0) {
        return null;
    }
    const clampedIndex = Math.min(
        Math.max(currentDayIndex, 0),
        targetDayShiftIds.length - 1,
    );
    return positionInRow(targetRow, targetDayShiftIds[clampedIndex]);
}

function filterVisibleShiftsByDay(
    shiftsByDay: ReadonlyMap<string, ReadonlyArray<Shift>>,
    visibleShifts: ReadonlyArray<Shift>,
): ReadonlyMap<string, ReadonlyArray<Shift>> {
    const visibleIds = new Set(visibleShifts.map((shift) => shift.id));
    const result = new Map<string, ReadonlyArray<Shift>>();
    for (const [dayKey, dayShifts] of shiftsByDay) {
        const dayVisible = dayShifts.filter((shift) =>
            visibleIds.has(shift.id),
        );
        if (dayVisible.length > 0) {
            result.set(dayKey, dayVisible);
        }
    }
    return result;
}

function getNearestDayShiftIds(
    row: CalendarNavigationMatrixRow,
    dayKey: string,
    weekDayKeys: ReadonlyArray<string>,
): ReadonlyArray<string> | undefined {
    const sameDay = row.shiftIdsByDay.get(dayKey);
    if (sameDay) {
        return sameDay;
    }

    const currentDayIndex = weekDayKeys.indexOf(dayKey);
    for (let distance = 1; distance < weekDayKeys.length; distance += 1) {
        const earlierDayKey = weekDayKeys[currentDayIndex - distance];
        if (earlierDayKey) {
            const earlier = row.shiftIdsByDay.get(earlierDayKey);
            if (earlier) {
                return earlier;
            }
        }

        const laterDayKey = weekDayKeys[currentDayIndex + distance];
        if (laterDayKey) {
            const later = row.shiftIdsByDay.get(laterDayKey);
            if (later) {
                return later;
            }
        }
    }

    return undefined;
}

function positionInRow(
    row: CalendarNavigationMatrixRow,
    shiftId: string | undefined,
): CalendarNavigationPosition | null {
    if (!shiftId) {
        return null;
    }
    const dayKey = row.dayKeyByShiftId.get(shiftId);
    if (!dayKey) {
        return null;
    }
    return { rowKey: row.rowKey, dayKey, shiftId };
}

export function bucketShiftsByStartDay(
    shifts: ReadonlyArray<Shift>,
    timezone: string,
): ReadonlyMap<string, ReadonlyArray<Shift>> {
    const shiftsByDay = new Map<string, Array<Shift>>();

    for (const shift of shifts) {
        const dateKey = toDateKey(new Date(shift.shift_start_time), timezone);
        const dayShifts = shiftsByDay.get(dateKey) ?? [];
        dayShifts.push(shift);
        shiftsByDay.set(dateKey, dayShifts);
    }

    for (const dayShifts of shiftsByDay.values()) {
        dayShifts.sort(compareShiftStartTimes);
    }

    return shiftsByDay;
}

export function groupShiftsByClient(
    shifts: ReadonlyArray<Shift>,
    timezone: string,
    clients: ReadonlyArray<OrgClientRead> = [],
    sortByLastName = false,
): ReadonlyArray<CalendarRow> {
    const groupedShifts = new Map<
        string,
        {
            readonly label: string;
            sublabel: string | null;
            readonly shifts: Array<Shift>;
        }
    >();

    for (const shift of shifts) {
        const key = shift.client_id
            ? `client:${shift.client_id}`
            : `name:${shift.patient_name}`;
        const existing = groupedShifts.get(key);

        if (existing) {
            existing.shifts.push(shift);
            existing.sublabel ??= shift.patient_office_name ?? null;
            continue;
        }

        groupedShifts.set(key, {
            label: shift.patient_name,
            sublabel: shift.patient_office_name ?? null,
            shifts: [shift],
        });
    }

    for (const client of clients) {
        const key = `client:${client.id}`;
        if (groupedShifts.has(key)) {
            continue;
        }

        groupedShifts.set(key, {
            label: `${client.first_name} ${client.last_name}`.trim(),
            sublabel: client.office_name,
            shifts: [],
        });
    }

    return Array.from(groupedShifts, ([key, group]) =>
        buildCalendarRow(
            key,
            group.label,
            group.sublabel,
            group.shifts,
            timezone,
        ),
    ).sort((left, right) =>
        comparePersonNames(left.label, right.label, sortByLastName),
    );
}

// The pinned-first "Open shifts" row in caregiver grouping; holds every
// shift with no caregiver assigned.
const OPEN_ROW_KEY = 'caregiver:unassigned';

export function groupShiftsByCaregiver(
    shifts: ReadonlyArray<Shift>,
    timezone: string,
    caregivers: ReadonlyArray<OrgCaregiverRecord> = [],
    sortByLastName = false,
): ReadonlyArray<CalendarRow> {
    // Name and office may be missing from a shift's embedded caregiver; they
    // stay null until some shift or roster entry provides them.
    const groupedShifts = new Map<
        string,
        {
            name: string | null;
            officeName: string | null;
            readonly shifts: Array<Shift>;
        }
    >();

    for (const shift of shifts) {
        const caregiverId = shift.caregiver?.id ?? shift.assigned_caregiver_id;
        const key = caregiverId ? `caregiver:${caregiverId}` : OPEN_ROW_KEY;
        const caregiverName = shift.caregiver?.name
            ? getCaregiverDisplayName(shift.caregiver.name)
            : null;
        const existing = groupedShifts.get(key);

        if (existing) {
            existing.shifts.push(shift);
            existing.name ??= caregiverName;
            existing.officeName ??= shift.caregiver?.office_name ?? null;
            continue;
        }

        groupedShifts.set(key, {
            name: caregiverName,
            officeName: shift.caregiver?.office_name ?? null,
            shifts: [shift],
        });
    }

    for (const caregiver of caregivers) {
        const key = `caregiver:${caregiver.id}`;
        const existing = groupedShifts.get(key);
        if (existing) {
            existing.name ??= getCaregiverDisplayName(caregiver.name);
            existing.officeName ??= caregiver.office_name ?? null;
            continue;
        }

        groupedShifts.set(key, {
            name: getCaregiverDisplayName(caregiver.name),
            officeName: caregiver.office_name ?? null,
            shifts: [],
        });
    }

    const openRow = buildCalendarRow(
        OPEN_ROW_KEY,
        'Open shifts',
        'Unassigned this week',
        groupedShifts.get(OPEN_ROW_KEY)?.shifts ?? [],
        timezone,
        true,
    );
    groupedShifts.delete(OPEN_ROW_KEY);
    const caregiverRows = Array.from(groupedShifts, ([key, group]) =>
        buildCalendarRow(
            key,
            group.name ?? 'Unknown caregiver',
            group.officeName,
            group.shifts,
            timezone,
        ),
    ).sort((left, right) =>
        comparePersonNames(left.label, right.label, sortByLastName),
    );
    return [openRow, ...caregiverRows];
}

export function getWeeklyShiftHours(shifts: ReadonlyArray<Shift>): number {
    const totalHours = shifts.reduce(
        (hours, shift) =>
            hours +
            (new Date(shift.shift_end_time).getTime() -
                new Date(shift.shift_start_time).getTime()) /
                3_600_000,
        0,
    );

    return Math.round(totalHours * 10) / 10;
}

export type WeeklyShiftCoverageHours = {
    readonly filledHours: number;
    readonly openHours: number;
};

/**
 * Splits non-cancelled shift time by caregiver assignment. Statuses such as
 * "not scheduled" can land in the attention bucket, but the shift remains
 * open until it actually has a caregiver.
 */
export function getWeeklyShiftCoverageHours(
    shifts: ReadonlyArray<Shift>,
): WeeklyShiftCoverageHours {
    const filledShifts: Array<Shift> = [];
    const openShifts: Array<Shift> = [];

    for (const shift of shifts) {
        const bucket = getShiftCalendarStatusBucket(shift);
        if (bucket === 'cancelled') {
            continue;
        }
        if (shift.caregiver?.id ?? shift.assigned_caregiver_id) {
            filledShifts.push(shift);
        } else {
            openShifts.push(shift);
        }
    }

    return {
        filledHours: getWeeklyShiftHours(filledShifts),
        openHours: getWeeklyShiftHours(openShifts),
    };
}

// Ordering for the caregiver axis: alphabetical, or overtime-first so the
// caregivers most over the weekly cap surface at the top.
export type CalendarCaregiverSort = 'name' | 'overtime';

/**
 * Hours a caregiver is scheduled beyond the organization's weekly cap
 * (recommendation defaults `max_weekly_hours` — the same threshold the
 * recommendation engine uses to flag overtime). Cancelled shifts don't count
 * toward the schedule. Returns 0 when the organization has no cap.
 */
export function getCaregiverOvertimeHours(
    shifts: ReadonlyArray<Shift>,
    maxWeeklyHours: number | null,
): number {
    if (maxWeeklyHours === null) {
        return 0;
    }
    const scheduledHours = getWeeklyShiftHours(
        shifts.filter(
            (shift) => getShiftCalendarStatusBucket(shift) !== 'cancelled',
        ),
    );
    // Re-round after subtracting so float noise can't produce "-0" or
    // "6.000000001" in the row header.
    return Math.max(0, Math.round((scheduledHours - maxWeeklyHours) * 10) / 10);
}

/**
 * Drops caregiver rows that are over the weekly cap, keeping the pinned
 * open-shifts row. With no cap configured every row passes through.
 */
export function filterCalendarRowsWithoutOvertime(
    rows: ReadonlyArray<CalendarRow>,
    maxWeeklyHours: number | null,
): ReadonlyArray<CalendarRow> {
    return rows.filter(
        (row) =>
            row.isOpenRow ||
            getCaregiverOvertimeHours(row.shifts, maxWeeklyHours) === 0,
    );
}

/**
 * Reorders caregiver rows so the most-overtime caregivers come first; ties
 * (including everyone with no overtime) keep their existing name order, and
 * the pinned open-shifts row stays on top.
 */
export function sortCalendarRowsByOvertime(
    rows: ReadonlyArray<CalendarRow>,
    maxWeeklyHours: number | null,
): ReadonlyArray<CalendarRow> {
    const overtimeByRowKey = new Map(
        rows.map((row) => [
            row.key,
            getCaregiverOvertimeHours(row.shifts, maxWeeklyHours),
        ]),
    );
    return [...rows].sort((left, right) => {
        if (left.isOpenRow !== right.isOpenRow) {
            return left.isOpenRow ? -1 : 1;
        }
        return (
            (overtimeByRowKey.get(right.key) ?? 0) -
            (overtimeByRowKey.get(left.key) ?? 0)
        );
    });
}

function compareShiftStartTimes(left: Shift, right: Shift): number {
    return left.shift_start_time.localeCompare(right.shift_start_time);
}

function buildCalendarRow(
    key: string,
    label: string,
    sublabel: string | null,
    shifts: Array<Shift>,
    timezone: string,
    isOpenRow = false,
): CalendarRow {
    const sortedShifts = shifts.sort(compareShiftStartTimes);
    return {
        key,
        label,
        sublabel,
        isOpenRow,
        shifts: sortedShifts,
        shiftsByDay: bucketShiftsByStartDay(sortedShifts, timezone),
    };
}

function isFillOutreachType(type: Outreach['outreach_type']): boolean {
    return (
        type === 'callout' ||
        type === 'recurring_shift' ||
        type === 'shift_discovery'
    );
}

function isClockReminderType(type: Outreach['outreach_type']): boolean {
    return type === 'clock_in' || type === 'clock_out';
}

function classifyShiftOutreachList(
    outreaches: ReadonlyArray<Outreach>,
    responseCountsByOutreachId?: Readonly<
        Record<string, OutreachResponseCounts>
    >,
): ShiftOutreachStatus {
    const fillOutreaches = outreaches.filter((outreach) =>
        isFillOutreachType(outreach.outreach_type),
    );
    const activeFillOutreaches = fillOutreaches.filter((outreach) =>
        isActiveOutreach(outreach.status),
    );
    const terminalFillOutreaches = fillOutreaches.filter(
        (outreach) => !isActiveOutreach(outreach.status),
    );
    const clockReminders = outreaches.filter((outreach) =>
        isClockReminderType(outreach.outreach_type),
    );
    const activeFillOutreach = getMostRecentOutreach(activeFillOutreaches);

    return {
        activeFillOutreach,
        activeFillResponseCounts:
            (activeFillOutreach &&
                responseCountsByOutreachId?.[activeFillOutreach.id]) ??
            null,
        latestTerminalFillOutreach: getMostRecentOutreach(
            terminalFillOutreaches,
        ),
        clockReminderOutreach: getMostRecentOutreach(clockReminders),
    };
}

// "Maria Garcia" → "Maria G." so chip rows stay short without losing which
// Maria said yes; single-word names pass through unchanged.
function getCompactCaregiverName(name: string): string {
    const parts = name.split(/\s+/u).filter(Boolean);
    const first = parts[0];
    if (first === undefined || parts.length < 2) {
        return name;
    }
    const lastInitial = parts[parts.length - 1]?.[0];
    return lastInitial === undefined ? first : `${first} ${lastInitial}.`;
}

/**
 * Name-first phrase fragment for the caregivers who said yes to one shift:
 * "Maria Garcia", "Maria Garcia, Ana Lopez", or "Maria Garcia +2" — callers
 * append the verb ("said yes"). `maxNames` bounds how many names are spelled
 * out; the remainder collapses into "+N" derived from the per-shift count, so
 * caregivers whose records were deleted (counted, but unnamed) stay visible
 * as overflow. Returns null when the payload carries no names, so callers can
 * fall back to their count-only copy.
 */
export function formatAcceptedCaregiverNames({
    counts,
    shiftId,
    maxNames,
    compact = false,
}: {
    readonly counts: OutreachResponseCounts | null | undefined;
    readonly shiftId: string;
    readonly maxNames: number;
    readonly compact?: boolean;
}): string | null {
    const accepted = getAcceptedCaregiversForShift(counts, shiftId);
    if (accepted.length === 0) {
        return null;
    }
    // The count can exceed the capped name list; never let it undercount.
    const acceptedCount = Math.max(
        getAcceptedCountForShift(counts, shiftId),
        accepted.length,
    );
    const shownNames = accepted.slice(0, maxNames).map((caregiver) => {
        const displayName = getCaregiverDisplayName(caregiver.caregiver_name);
        return compact ? getCompactCaregiverName(displayName) : displayName;
    });
    const overflowCount = acceptedCount - shownNames.length;
    const namesPart = shownNames.join(', ');
    return overflowCount > 0 ? `${namesPart} +${overflowCount}` : namesPart;
}

// Some EHRs send caregiver names as "Last, First"; flip those to natural
// "First Last" for display and pass any comma-free name through unchanged.
export function getCaregiverDisplayName(name: string): string {
    const trimmedName = name.trim();
    const commaIndex = trimmedName.indexOf(',');
    if (commaIndex === -1) {
        return trimmedName;
    }

    const last = trimmedName.slice(0, commaIndex).trim();
    const first = trimmedName.slice(commaIndex + 1).trim();
    return `${first} ${last}`.trim();
}

function getMostRecentOutreach(
    outreaches: ReadonlyArray<Outreach>,
): Outreach | null {
    return outreaches.reduce<Outreach | null>((latest, outreach) => {
        if (!latest || outreach.created_at > latest.created_at) {
            return outreach;
        }
        return latest;
    }, null);
}

function isValidDateKey(value: string | undefined): value is string {
    if (!value || !DATE_KEY_PATTERN.test(value)) {
        return false;
    }

    const parsedDate = parseDateKey(value);
    return (
        !Number.isNaN(parsedDate.getTime()) &&
        toDateKey(parsedDate, 'UTC') === value
    );
}

function parseDateKey(dateKey: string): Date {
    return new Date(`${dateKey}T00:00:00.000Z`);
}
